import * as React from "react";
import { DonationMetadata, JourneyContext, Member } from "@flow/common/types";
import { Text } from "@flow/lib/i18n";
import { validateSmartDebitDirectDebitDetails, createSmartDebitDirectDebit } from "../../common/routes";
import { sortCodePattern, accountNumberPattern, validSortCode } from "../../common/utils";
import { v4 as uuidv4 } from "uuid";
import { Result } from "safe-portals";
import { loadCaptcha, captchaExecute } from "@flow/frontend/member/captcha";
import { Alert, Box, Button, Grid, TextField, Typography } from "@mui/material";
import { memberTheme } from "@flow/frontend/member/memberTheme";
import { EmailInput } from "@flow/frontend/member/components/EmailInput";
import { RequiredTextArea } from "@flow/frontend/member/components/RequiredTextArea";
import { MemberLoadingSpinner } from "@flow/frontend/member/components/MemberLoadingSpinner";

function AccountNumberInput(props: {
  value?: string;
  onChange: (e: React.ChangeEvent<HTMLInputElement> | string) => void;
  label?: string | Text;
  style?: React.CSSProperties;
}) {
  return (
    <Box className="form-group">
      <RequiredTextArea
        id="account-number"
        onChange={props.onChange}
        value={props.value}
        inputPattern={accountNumberPattern}
        errorFeedbackText="Account number must be 8 digits long"
        label="Account Number"
      />
    </Box>
  );
}

function SortCodeInput(props: { value?: string; onChange: (v: string) => void; label?: string | Text }) {
  const [valid, setValid] = React.useState(false);
  const [dirty, setDirty] = React.useState(false);

  const onChange = (e: React.ChangeEvent<HTMLInputElement> | string) => {
    const v = typeof e === "string" ? e : e.target.value;
    if (v.length === 2 || v.length === 5) {
      props.onChange(`${v}-`);
    } else {
      props.onChange(v);
    }
  };

  const onFormInteraction = (e: React.FocusEvent<HTMLInputElement>) => {
    const inputValue = e.currentTarget.value;
    if (inputValue.length === 6) {
      setValid(validSortCode(inputValue));
      setDirty(true);
    }
  };

  return (
    <Box className="form-group">
      <TextField
        id="sort-code"
        fullWidth
        required
        label={props.label || "Sort Code"}
        value={props.value}
        onChange={(e) => {
          // Dirty false so error disappears while the user is typing
          setDirty(false);
          onChange(e.target.value);
        }}
        onBlur={onFormInteraction}
        inputProps={{
          pattern: sortCodePattern,
          minLength: 6,
          maxLength: 8,
          title: "Sort code must be 6 digits long",
        }}
        error={dirty}
        aria-invalid={!valid}
        helperText={dirty ? "Sort code must be 6 digits long" : ""}
        variant="outlined"
      />
    </Box>
  );
}

function ConfirmDirectDebit(props: {
  member: Member;
  amount: number;
  sortCode: string;
  accountNumber: string;
  referenceNumber: string;
  onConfirm: () => void;
}) {
  return (
    <>
      <Box mt={2} mb={2}>
        <Typography id="plugin.donate.smart_debit.confirm_message" variant="body1" paragraph>
          You are setting up a direct debit of £{props.amount} / Month <br />
          Account number: <span className="posthog-mask-text">{props.accountNumber}</span> <br />
          Sort code: <span className="posthog-mask-text">{props.sortCode}</span> <br />
          BACS Reference: <span className="posthog-mask-text">{props.referenceNumber}</span> <br />
          You will receive a confirmation email.
        </Typography>
      </Box>
      <Button id="donate-confirm-dd" onClick={props.onConfirm}>
        <Typography id="plugin.donate.smart_debit.confirm_button_text" variant="button">
          Confirm Direct Debit
        </Typography>
      </Button>
    </>
  );
}

const generateReferenceNumber = (): string => {
  // Reference number spec:
  // https://apidocs.smartdebit.co.uk/#4d8a7173-5301-41e1-b171-7d4fde1fef6b
  return uuidv4().replace(/-/g, "").slice(0, 18);
};

export function SmartDebitCheckout(props: {
  ctx: JourneyContext;
  variant_idx: number;
  amount: number;
  metadata: DonationMetadata;
  hide_donation: boolean;
  isRegular: boolean;
  onClickBack: () => void;
  onPaymentSuccess: (donation_attempt_guid: string, member: Member) => void;
  form: React.MutableRefObject<HTMLFormElement | null>;
  redacted_prefill?: { name?: string; has_email: boolean; has_postcode: boolean };
  useMemberPrefill: boolean;
  setUseMemberPrefill: React.Dispatch<React.SetStateAction<boolean>>;
  action_guid: string;
}) {
  const [processing, setProcessing] = React.useState(false);
  const [member, setMember] = React.useState(props.ctx.member);

  const [accountNumber, setAccountNumber] = React.useState<string | undefined>(undefined);
  const [sortCode, setSortCode] = React.useState<string | undefined>(undefined);
  const [referenceNumber] = React.useState<string>(generateReferenceNumber());
  const [errorMessage, setErrorMessage] = React.useState<string | undefined>(undefined);
  const [showConfirm, setShowConfirm] = React.useState(false);

  React.useEffect(() => {
    loadCaptcha();
  }, []);

  const handleAccountNumberChange = (e: React.ChangeEvent<HTMLInputElement> | string) => {
    const value = typeof e === "string" ? e : e.target.value;
    setAccountNumber(value);
  };

  function rejectPrefill(e: React.MouseEvent) {
    e.preventDefault();
    props.setUseMemberPrefill(false);
  }

  const handleClickDonate = async (e: React.MouseEvent<HTMLElement>) => {
    e.preventDefault();
    const captcha_token = await captchaExecute("smart_debit_donation_submit");

    setProcessing(true);
    setErrorMessage(undefined);

    const { name, email } = member;

    if (!props.useMemberPrefill && (name === undefined || email === undefined)) {
      setProcessing(false);
      setErrorMessage("Please check your name and email then try again");
      return;
    }

    if (accountNumber === undefined || sortCode === undefined) {
      setProcessing(false);
      setErrorMessage("Please check your account number and sort code then try again");
      return;
    }

    const result = await validateSmartDebitDirectDebitDetails.call({
      member: { name: name, email: email },
      use_prefill_member_data: props.useMemberPrefill,
      accountDetails: {
        account_number: accountNumber,
        sort_code: sortCode.replace(/-/g, ""),
        account_name: name,
      },
      smartDebitParams: {
        amount: Math.round(props.amount * 100),
        referenceNumber: referenceNumber,
        customerId: props.action_guid,
      },
      captcha_token: captcha_token,
      action_guid: props.action_guid,
    });
    setProcessing(false);

    if (Result.isErr(result)) {
      setErrorMessage("Something went wrong please check your details and try again");
    } else {
      setShowConfirm(true);
    }
  };

  const handleConfirmDonate = async () => {
    const { name, email } = member;

    if (!props.useMemberPrefill && (name === undefined || email === undefined)) {
      setProcessing(false);
      setErrorMessage("Please check your name and email then try again");
      return;
    }

    if (accountNumber === undefined || sortCode === undefined) {
      setShowConfirm(false);
      setErrorMessage("Please check your account number and sort code then try again");
      return;
    }

    setProcessing(true);

    const result = await createSmartDebitDirectDebit.call({
      action_guid: props.action_guid,
      variant_idx: props.variant_idx,
      journey_context: { ...props.ctx, member },
      use_prefill_member_data: props.useMemberPrefill,
      accountDetails: {
        account_number: accountNumber,
        sort_code: sortCode,
        account_name: name,
      },
      smartDebitParams: {
        amount: Math.round(props.amount * 100),
        referenceNumber: referenceNumber,
        customerId: props.action_guid,
      },
      metadata: props.metadata,
      hide_donation: props.hide_donation,
    });

    setProcessing(false);
    setShowConfirm(false);

    if (Result.isErr(result) || result.ok.donate_action_guid === undefined) {
      setErrorMessage("Something went wrong please check your details and try again");
    } else {
      props.onPaymentSuccess(result.ok.donate_action_guid, member);
    }
  };

  return (
    <>
      {processing && <MemberLoadingSpinner />}

      {!processing && !showConfirm && (
        <>
          {props.redacted_prefill && props.useMemberPrefill ? (
            <Box className="petition-prefill-prompt">
              <Box sx={{ p: 3 }}>
                You&apos;re donating as <strong className="posthog-mask-text">{props.redacted_prefill.name}</strong>
                <Button variant="text" onClick={rejectPrefill}>
                  Not you? Click here
                </Button>
              </Box>
            </Box>
          ) : (
            <>
              <Grid container spacing={1}>
                <Grid item xs={12} sx={{ pb: "20px", mt: "10px" }}>
                  <TextField
                    variant="outlined"
                    fullWidth
                    id="member-name"
                    defaultValue={member.name || ""}
                    label="Name"
                    onChange={(event) => setMember({ ...member, name: event.target.value })}
                  />
                </Grid>
                <Grid item xs={12} sx={{ pb: "20px", mb: "-20px" }}>
                  <EmailInput value={member.email || ""} onChange={(email) => setMember({ ...member, email })} />
                </Grid>
                <Grid item xs={12} md={6} sx={{ pb: "20px", mb: "-30px" }}>
                  <AccountNumberInput
                    value={accountNumber}
                    onChange={handleAccountNumberChange}
                    label="Account Number"
                  />
                </Grid>
                <Grid item xs={12} md={6} sx={{ pb: "10px" }}>
                  <SortCodeInput value={sortCode} onChange={setSortCode} label="Sort Code" />
                </Grid>
              </Grid>
            </>
          )}
          {errorMessage && (
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <Box mb={2}>
                  <Alert severity="warning">{errorMessage}</Alert>
                </Box>
              </Grid>
            </Grid>
          )}

          <Grid container spacing={1}>
            <Grid item xs={12} sm={6} sx={{ pb: "20px" }}>
              <Button
                onClick={(e) => props.onClickBack()}
                type="button"
                sx={{
                  backgroundColor: memberTheme.palette.greys.grey300,
                  color: "#000000",
                  width: "100%",
                }}
              >
                Change amount
              </Button>
            </Grid>
            <Grid item xs={12} sm={6} sx={{ pb: "10px" }}>
              <form ref={props.form}>
                <Button
                  id="donate-setup-dd"
                  onClick={handleClickDonate}
                  type="submit"
                  sx={{
                    width: "100%",
                    backgroundColor: memberTheme.palette.primary.main,
                    color: memberTheme.palette.primary.contrastText,
                  }}
                >
                  {`Direct Debit £${props.amount} / month`}
                </Button>
              </form>
            </Grid>
          </Grid>
        </>
      )}

      {!processing && showConfirm && sortCode && accountNumber && (
        <ConfirmDirectDebit
          member={member}
          amount={props.amount}
          sortCode={sortCode}
          accountNumber={accountNumber}
          referenceNumber={referenceNumber}
          onConfirm={handleConfirmDonate}
        />
      )}
    </>
  );
}
